import type { CrmPerson, CrmVersionenListe, Me } from '@portal/contracts';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';
import { crmApi, HttpError } from '../../api.js';
import { onLinkClick } from '../../router.js';
import { Problem } from '../ModulePage.js';
import {
  ADRESSE_FELDER, CRM_PFAD, EINWILLIGUNG_FELDER, FEST_BEIM_AENDERN, KONTAKTKANAL_FELDER, LEER, PERSON_GRUPPEN, ABGLEICH_STUFE_LABEL, ABGLEICH_WIRKUNG_LABEL, quelleAnzeige,
  darfBearbeiten, formatZeit, mitFilialen, wertLabel, wertText, type FeldDef,
} from './crm-felder.js';
import { Formular, Werte } from './Formular.js';
import { useFilialen } from './useFilialen.js';
import { Filter, Seiten, VersionenTabelle } from './Versionen.js';

const TABS = [
  { key: 'stammdaten', label: 'Stammdaten' },
  { key: 'adressen', label: 'Adressen' },
  { key: 'kontaktkanaele', label: 'Kontaktkanäle' },
  { key: 'einwilligungen', label: 'Einwilligungen' },
  { key: 'versionen', label: 'Versionen' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

type Laden = { kind: 'laden' } | { kind: 'fehler'; status: number } | { kind: 'ok'; p: CrmPerson };

function anzeigename(p: CrmPerson): string {
  const n = [p.vorname, p.nachname].filter(Boolean).join(' ');
  return n || p.name || p.firma || 'Ohne Namen';
}

/** Datensatz `/crm/kunden/:id` mit Tabs (role=tablist); Bearbeiten nur ab Stufe bearbeiten */
export function Datensatz({ id, me }: { id: string; me: Me }) {
  const [state, setState] = useState<Laden>({ kind: 'laden' });
  const [tab, setTab] = useState<TabKey>(() => (new URLSearchParams(window.location.search).get('tab') as TabKey) || 'stammdaten');
  const [meldung, setMeldung] = useState<string | null>(null);
  const schreiben = darfBearbeiten(me);

  const laden = useCallback(async () => {
    try {
      setState({ kind: 'ok', p: await crmApi.person(id) });
    } catch (err) {
      setState({ kind: 'fehler', status: err instanceof HttpError ? err.status : 0 });
    }
  }, [id]);
  useEffect(() => void laden(), [laden]);

  if (state.kind === 'laden') return <p className="sh-status" aria-busy="true">Kunde wird geladen …</p>;
  if (state.kind === 'fehler') {
    return state.status === 404 ? (
      <section className="crm">
        <BackLink />
        <div className="pb-card crm-hinweis" role="alert">
          Diesen Kunden gibt es nicht (mehr). Das Änderungsprotokoll bleibt unter „Änderungsprotokoll“ einsehbar.
        </div>
      </section>
    ) : (
      <Problem status={state.status} />
    );
  }
  const p = state.p;
  const gespeichert = (text: string) => {
    setMeldung(text);
    void laden();
  };

  return (
    <section className="crm" aria-labelledby="crm-kunde-titel">
      <BackLink />
      <div className="ap-head">
        <div>
          <p className="crm-crumb">CRM · Kunde</p>
          <h1 className="ap-title" id="crm-kunde-titel">
            {anzeigename(p)}
          </h1>
          <p className="ap-sub">
            {quelleAnzeige(p)}
            {p.quellenId ? ` · ${p.quellenId}` : ''} · zuletzt geändert {formatZeit(p.geaendertAt)}
          </p>
        </div>
        <div className="crm-badges">
          {p.deaktiviertAt && <span className="crm-badge crm-badge--aus">Deaktiviert</span>}
          {p.blacklist && <span className="crm-badge crm-badge--warn">Blacklist</span>}
          {p.statusNeo === 'gesperrt' && <span className="crm-badge crm-badge--warn">Gesperrt</span>}
          {p.abgleichPruefen && <span className="crm-badge crm-badge--warn">Abgleich prüfen</span>}
        </div>
      </div>
      {meldung && (
        <p className="crm-hinweis crm-hinweis--ok" role="status">
          {meldung}
        </p>
      )}
      <Tabs aktiv={tab} onChange={setTab} />
      <div role="tabpanel" id={`crm-panel-${tab}`} aria-labelledby={`crm-tab-${tab}`} tabIndex={0} className="crm-panel">
        {tab === 'stammdaten' && <Stammdaten p={p} schreiben={schreiben} onGespeichert={gespeichert} />}
        {tab === 'adressen' && (
          <Unterliste
            titel="Adressen"
            einzahl="Adresse"
            defs={ADRESSE_FELDER}
            fest={FEST_BEIM_AENDERN.adresse}
            zeilen={p.adressen as unknown as Record<string, unknown>[]}
            schreiben={schreiben}
            kopf={(a) => [a.typ ? wertLabel('typ', a.typ) : LEER, [a.strasse, a.hausnummer].filter(Boolean).join(' '), [a.plz, a.ort].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || 'Adresse'}
            anlegen={(b) => crmApi.adresseAnlegen(p.id, b)}
            aendern={(aid, b) => crmApi.adresseAendern(p.id, aid, b)}
            onGespeichert={gespeichert}
            hinweis="Eine Adresse wird nicht gelöscht — zum Beenden „Gültig bis“ setzen."
          />
        )}
        {tab === 'kontaktkanaele' && (
          <Unterliste
            titel="Kontaktkanäle"
            einzahl="Kontaktkanal"
            defs={KONTAKTKANAL_FELDER}
            fest={FEST_BEIM_AENDERN.kontaktkanal}
            zeilen={p.kontaktkanaele as unknown as Record<string, unknown>[]}
            schreiben={schreiben}
            kopf={(k) => `${wertLabel('kanal', k.kanal)} · ${wertText(k.wertNorm ?? k.wertRoh)}`}
            anlegen={(b) => crmApi.kontaktkanalAnlegen(p.id, b)}
            aendern={(kid, b) => crmApi.kontaktkanalAendern(p.id, kid, b)}
            onGespeichert={gespeichert}
            hinweis="Die Normform wird serverseitig gebildet (E-Mail klein geschrieben, Telefon international)."
          />
        )}
        {tab === 'einwilligungen' && (
          <Unterliste
            titel="Einwilligungen"
            einzahl="Einwilligung"
            defs={EINWILLIGUNG_FELDER}
            fest={FEST_BEIM_AENDERN.einwilligung}
            zeilen={p.einwilligungen as unknown as Record<string, unknown>[]}
            schreiben={schreiben}
            kopf={(e) => `${wertLabel('kanal', e.kanal)} · ${wertLabel('status', e.status)}`}
            anlegen={(b) => crmApi.einwilligungAnlegen(p.id, b)}
            aendern={(eid, b) => crmApi.einwilligungAendern(p.id, eid, b)}
            onGespeichert={gespeichert}
            hinweis="Je Kanal eine Einwilligung; ein Widerruf wird als Status „widerrufen“ mit Datum festgehalten."
          />
        )}
        {tab === 'versionen' && <PersonVersionen id={p.id} />}
      </div>
    </section>
  );
}

function BackLink() {
  return (
    <a className="crm-back" href={CRM_PFAD} onClick={onLinkClick(CRM_PFAD)}>
      <ArrowLeft size={15} aria-hidden /> Zur Kundenliste
    </a>
  );
}

function Tabs({ aktiv, onChange }: { aktiv: TabKey; onChange(t: TabKey): void }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: KeyboardEvent, i: number) => {
    const n = TABS.length;
    let z = -1;
    if (e.key === 'ArrowRight') z = (i + 1) % n;
    else if (e.key === 'ArrowLeft') z = (i - 1 + n) % n;
    else if (e.key === 'Home') z = 0;
    else if (e.key === 'End') z = n - 1;
    if (z < 0) return;
    e.preventDefault();
    onChange(TABS[z]!.key);
    refs.current[z]?.focus();
  };
  return (
    <div role="tablist" aria-label="Bereiche des Kundendatensatzes" className="crm-tabs">
      {TABS.map((t, i) => (
        <button
          key={t.key}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          id={`crm-tab-${t.key}`}
          aria-selected={aktiv === t.key}
          aria-controls={`crm-panel-${t.key}`}
          tabIndex={aktiv === t.key ? 0 : -1}
          className="crm-tab"
          onClick={() => onChange(t.key)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------ Stammdaten ------------------------------------------------ */

function Stammdaten({ p, schreiben, onGespeichert }: { p: CrmPerson; schreiben: boolean; onGespeichert(text: string): void }) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [deaktivieren, setDeaktivieren] = useState(false);
  // #851 Stammfiliale immer als Auswahl aus kunden.filiale (Laden/Fehler: gesperrt, Wert bleibt, „Erneut laden“)
  const filialen = useFilialen();
  const gruppen = PERSON_GRUPPEN.map((g) => ({ ...g, felder: mitFilialen(g.felder, filialen.zustand, p.stammfiliale, filialen.erneut) }));
  const alleFelder = gruppen.flatMap((g) => g.felder);
  const werte = p as unknown as Record<string, unknown>;

  if (bearbeiten) {
    return (
      <div className="pb-card crm-karte">
        <h2>Stammdaten bearbeiten</h2>
        <Formular
          defs={alleFelder}
          original={werte}
          fest={FEST_BEIM_AENDERN.person}
          titel="Stammdaten bearbeiten"
          onSpeichern={async (patch, kommentar) => {
            await crmApi.aendern(p.id, kommentar ? { ...patch, kommentar } : patch);
            setBearbeiten(false);
            onGespeichert('Stammdaten gespeichert. Die Änderung steht im Änderungsprotokoll.');
          }}
          onAbbrechen={() => setBearbeiten(false)}
        />
      </div>
    );
  }
  return (
    <>
      {gruppen.map((g) => (
        <div key={g.titel} className="pb-card crm-karte">
          <h2>{g.titel}</h2>
          <Werte defs={g.felder} werte={werte} />
          {g.titel === 'Quellen' && (
            <>
              <h3 className="crm-untertitel">Verweise in Quellsystemen</h3>
              {p.quellen.length === 0 ? (
                <p className="crm-muted">Keine Verweise.</p>
              ) : (
                <ul className="crm-quellen">
                  {p.quellen.map((q) => (
                    <li key={q.id}>
                      <strong title={q.system}>{wertLabel('system', q.system)}</strong> · Fremd-ID {q.fremdId} · zweite Nummer {q.fremdNr2 ?? LEER}
                      <span className="crm-muted">
                        {' '}
                        · zuletzt gesehen {formatZeit(q.zuletztGesehen)} · im Quellsystem gelöscht: {q.imQuellsystemGeloescht ? 'ja' : 'nein'}
                      </span>
                      <span className="crm-tech"> · Verweis-ID {q.id}</span>
                    </li>
                  ))}
                </ul>
              )}
              {(p.abgleich ?? []).length > 0 && (
                <>
                  <h3 className="crm-untertitel">Abgleich</h3>
                  <ul className="crm-quellen">
                    {(p.abgleich ?? []).map((a) => (
                      <li key={a.id}>
                        <strong>Stufe {a.stufe}</strong> · {ABGLEICH_STUFE_LABEL[a.stufe] ?? LEER} · {ABGLEICH_WIRKUNG_LABEL[a.wirkung] ?? a.wirkung}
                        <span className="crm-muted"> · Status {a.status}{a.kandidatPersonId ? ' · ähnliche Person vorhanden' : ''}</span>
                        <span className="crm-tech"> · Abgleich-ID {a.id}</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>
      ))}
      {schreiben && (
        <div className="crm-leiste">
          {!p.deaktiviertAt && (
            <button type="button" className="pb-button pb-button--quiet crm-gefahr" onClick={() => setDeaktivieren(true)}>
              Deaktivieren
            </button>
          )}
          <button type="button" className="pb-button" onClick={() => setBearbeiten(true)}>
            Stammdaten bearbeiten
          </button>
        </div>
      )}
      {deaktivieren && (
        <Bestaetigung
          titel="Kunden deaktivieren?"
          text="Der Datensatz bleibt erhalten und wird nicht mehr in der Kundenliste geführt. Die Deaktivierung wird im Änderungsprotokoll festgehalten."
          aktion="Deaktivieren"
          onBestaetigt={async (kommentar) => {
            await crmApi.deaktivieren(p.id, kommentar ? { kommentar } : {});
            setDeaktivieren(false);
            onGespeichert('Kunde deaktiviert.');
          }}
          onClose={() => setDeaktivieren(false)}
        />
      )}
    </>
  );
}

/** Bestätigungsdialog (native <dialog>, Fokus im Dialog, Esc schließt) */
function Bestaetigung({ titel, text, aktion, onBestaetigt, onClose }: { titel: string; text: string; aktion: string; onBestaetigt(kommentar: string): Promise<void>; onClose(): void }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titelId = useId();
  const kId = useId();
  const [kommentar, setKommentar] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  return (
    <dialog
      ref={ref}
      className="crm-dialog"
      aria-labelledby={titelId}
      aria-modal="true"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <h2 id={titelId}>{titel}</h2>
      <p>{text}</p>
      <div className="crm-feld">
        <label htmlFor={kId}>Kommentar (optional)</label>
        <input id={kId} type="text" maxLength={500} value={kommentar} onChange={(e) => setKommentar(e.target.value)} />
      </div>
      {fehler && (
        <p className="crm-hinweis crm-hinweis--fehler" role="alert">
          {fehler}
        </p>
      )}
      <div className="crm-leiste">
        <button type="button" className="pb-button pb-button--quiet" onClick={onClose} disabled={busy}>
          Abbrechen
        </button>
        <button
          type="button"
          className="pb-button crm-gefahr"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            setFehler(null);
            onBestaetigt(kommentar.trim()).catch((err: unknown) => {
              setFehler(err instanceof HttpError ? (err.text ?? `Nicht möglich (${err.status}).`) : 'Nicht möglich. Bitte versuchen Sie es erneut.');
              setBusy(false);
            });
          }}
        >
          {busy ? 'Bitte warten …' : aktion}
        </button>
      </div>
    </dialog>
  );
}

/* ------------------------------------------------ Unterressourcen ------------------------------------------------ */

function Unterliste({
  titel,
  einzahl,
  defs,
  fest,
  zeilen,
  schreiben,
  kopf,
  anlegen,
  aendern,
  onGespeichert,
  hinweis,
}: {
  titel: string;
  einzahl: string;
  defs: readonly FeldDef[];
  fest: readonly string[];
  zeilen: Record<string, unknown>[];
  schreiben: boolean;
  kopf(z: Record<string, unknown>): string;
  anlegen(body: Record<string, unknown>): Promise<unknown>;
  aendern(id: string, body: Record<string, unknown>): Promise<unknown>;
  onGespeichert(text: string): void;
  hinweis: string;
}) {
  const [modus, setModus] = useState<{ art: 'neu' } | { art: 'aendern'; id: string } | null>(null);
  const mitKommentar = (patch: Record<string, unknown>, kommentar: string) => (kommentar ? { ...patch, kommentar } : patch);

  if (modus?.art === 'neu') {
    return (
      <div className="pb-card crm-karte">
        <h2>{einzahl} anlegen</h2>
        <Formular
          defs={defs}
          original={{}}
          neu
          titel={`${einzahl} anlegen`}
          onSpeichern={async (patch, kommentar) => {
            await anlegen(mitKommentar(patch, kommentar));
            setModus(null);
            onGespeichert(`${einzahl} angelegt.`);
          }}
          onAbbrechen={() => setModus(null)}
        />
      </div>
    );
  }
  return (
    <>
      <p className="crm-muted crm-hinweistext">{hinweis}</p>
      {zeilen.length === 0 && <div className="pb-card crm-karte crm-muted">Keine {titel.toLowerCase()} erfasst.</div>}
      {zeilen.map((z) => {
        const zid = String(z.id);
        const offen = modus?.art === 'aendern' && modus.id === zid;
        return (
          <div key={zid} className="pb-card crm-karte">
            <div className="crm-karte__kopf">
              <h2>{kopf(z)}</h2>
              {schreiben && !offen && (
                <button type="button" className="pb-button pb-button--quiet" onClick={() => setModus({ art: 'aendern', id: zid })}>
                  Bearbeiten
                </button>
              )}
            </div>
            {offen ? (
              <Formular
                defs={defs}
                original={z}
                fest={fest}
                titel={`${einzahl} bearbeiten`}
                onSpeichern={async (patch, kommentar) => {
                  await aendern(zid, mitKommentar(patch, kommentar));
                  setModus(null);
                  onGespeichert(`${einzahl} gespeichert.`);
                }}
                onAbbrechen={() => setModus(null)}
              />
            ) : (
              <Werte defs={defs} werte={z} />
            )}
          </div>
        );
      })}
      {schreiben && !modus && (
        <div className="crm-leiste">
          <button type="button" className="pb-button" onClick={() => setModus({ art: 'neu' })}>
            {einzahl} anlegen
          </button>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------ Versionen je Person ------------------------------------------------ */

function PersonVersionen({ id }: { id: string }) {
  const [filter, setFilter] = useState({ feld: '', akteur: '', seite: 1 });
  const [daten, setDaten] = useState<CrmVersionenListe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [versuch, setVersuch] = useState(0);
  useEffect(() => {
    let alive = true;
    // Fehlerzustand bei jedem neuen Abruf zurücksetzen (sonst bliebe eine alte Meldung nach erfolgreichem Filterwechsel stehen)
    setFehler(null);
    crmApi.personVersionen(id, filter).then(
      (d) => {
        if (!alive) return;
        setDaten(d);
        setFehler(null);
      },
      (err: unknown) => alive && setFehler(err instanceof HttpError && err.status === 403 ? 'Für das Änderungsprotokoll fehlt Ihnen die Berechtigung.' : 'Das Änderungsprotokoll konnte nicht geladen werden.'),
    );
    return () => {
      alive = false;
    };
  }, [id, filter, versuch]);
  return (
    <>
      <div className="crm-filterleiste">
        <Filter label="Feld" wert={filter.feld} onChange={(feld) => setFilter({ ...filter, feld, seite: 1 })} />
        <Filter label="Akteur" wert={filter.akteur} onChange={(akteur) => setFilter({ ...filter, akteur, seite: 1 })} />
      </div>
      {fehler ? (
        <p className="crm-hinweis crm-hinweis--fehler" role="alert">
          {fehler}{' '}
          <button type="button" className="pb-button pb-button--quiet" onClick={() => setVersuch((n) => n + 1)}>
            Erneut laden
          </button>
        </p>
      ) : !daten ? (
        <p className="sh-status" aria-busy="true">
          Änderungsprotokoll wird geladen …
        </p>
      ) : (
        <>
          <VersionenTabelle daten={daten} />
          <Seiten seite={daten.seite} groesse={daten.groesse} gesamt={daten.gesamt} onSeite={(seite) => setFilter({ ...filter, seite })} />
        </>
      )}
    </>
  );
}
